/**
 * The end of a concept's Platform read points at its Certification version
 * (Coleman, 2026-10-08): a dark, blurred preview of the next section that
 * only exists in the cert, with a lock; tapping it explains that the
 * Coaches Certification goes deeper.
 *
 * Everything in the preview is the cert lesson's own words: the sections
 * only the Certification has (depth.js) and its first exercise, so the
 * teaser can't promise something the cert doesn't contain. The only new
 * words are the lock and the pop-up.
 *
 * Who sees what:
 *   enrolled            an open card that goes straight to the lesson
 *   not enrolled, web   the locked preview and the pop-up, which links to
 *                       the Certification page
 *   not enrolled, iOS   nothing. The cert is sold outside Apple's in-app
 *                       purchase, and the app's reader-app rule (see
 *                       CertificationPage) is no cert pitch or checkout on
 *                       iOS. CLAUDE.md, Apple guardrail B (anti-steering).
 *   admin               the locked version, so it can be seen as coaches
 *                       see it, with a note saying so
 *
 * Until a concept's cert lesson is placed in CERT_UNITS, only admins see it:
 * there is nowhere yet to send a coach.
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Lock, ArrowRight, X } from "lucide-react";
import { GOLD } from "../constants/theme";
import { CERT_UNITS, CERT_DEEP_DIVES } from "../certification/constants";
import { CONCEPT_CERT_MODULES } from "./index";
import { certOnlySections } from "./depth";
import { isNative } from "../../utils/platform";

/** Where a cert module lives in the certification, or null. */
export function certLessonFor(certKey) {
  if (!certKey) return null;
  for (const u of [...CERT_UNITS, CERT_DEEP_DIVES]) {
    const l = (u.lessons || []).find(x => x.masterclassKey === certKey);
    if (l) return { unitId: u.id, lessonId: l.id, unitTitle: u.title };
  }
  return null;
}

/** The first thing in a cert module that asks the coach for something. */
function firstExercise(mod) {
  for (const ch of mod.chapters || []) {
    for (const b of ch.blocks || []) {
      if (["writtenResponse", "multipleChoice", "diagnostic"].includes(b.kind)) return b;
    }
  }
  return null;
}

/** A concept's Platform module with the teaser placed before its related
 *  links. The teaser is chrome, not lesson text, so it is added here at
 *  render time and never stored in the generated (verbatim) module. */
export function withCertTeaser(module, certKey, { enrolled = false, admin = false } = {}) {
  if (!module || !certKey || !CONCEPT_CERT_MODULES[certKey]) return module;
  if (!enrolled && !admin && isNative()) return module;
  if (!admin && !certLessonFor(certKey)) return module;
  const teaser = { kind: "certTeaser", certKey, enrolled: enrolled && !admin, admin };
  return {
    ...module,
    chapters: (module.chapters || []).map((ch, i, all) => {
      if (i !== all.length - 1) return ch;
      const blocks = [...(ch.blocks || [])];
      // The closing related concepts; a lesson can also link one mid-way.
      const at = blocks.map(b => b.kind).lastIndexOf("relatedConcepts");
      blocks.splice(at === -1 ? blocks.length : at, 0, teaser);
      return { ...ch, blocks };
    }),
  };
}

const plain = (s) => String(s || "").replace(/\*\*/g, "");

export function CertTeaser({ C, certKey, enrolled = false, admin = false }) {
  const [open, setOpen] = useState(false);
  const mod = CONCEPT_CERT_MODULES[certKey];
  const where = certLessonFor(certKey);
  if (!mod) return null;
  // The sections only the Certification has, which is what's being locked.
  const chapters = certOnlySections(mod).slice(0, 6);
  const ex = firstExercise(mod);
  const goLesson = () => {
    window.location.hash = where ? `#/certified/lesson/${where.unitId}/${where.lessonId}` : "#/certified";
  };

  const eyebrow = { fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: GOLD };

  if (enrolled) {
    return (
      <button type="button" className="btn" onClick={goLesson}
        style={{ width: "100%", textAlign: "left", margin: "28px 0 8px", padding: "18px 20px", borderRadius: 14,
          border: `1px solid ${GOLD}55`, background: `linear-gradient(135deg, ${GOLD}14, ${GOLD}04)`, color: C.text,
          fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...eyebrow, display: "block", marginBottom: 6 }}>In your Certification · {mod.duration}</span>
          <span style={{ display: "block", fontSize: 17, fontWeight: 800 }}>{mod.title}{mod.sub ? `: ${mod.sub}` : ""}</span>
          <span style={{ display: "block", fontSize: 13, color: C.textMid, marginTop: 4 }}>{chapters.slice(0, 4).join(" · ")}</span>
        </span>
        <ArrowRight size={18} color={GOLD}/>
      </button>
    );
  }

  return (
    <div style={{ margin: "30px 0 8px" }}>
      <div style={{ ...eyebrow, marginBottom: 10 }}>Next, in the Coaches Certification</div>
      <button type="button" onClick={() => setOpen(true)} aria-label="Locked: part of the BAM Coaches Certification"
        style={{ position: "relative", display: "block", width: "100%", padding: 0, border: `1px solid ${C.border}`,
          borderRadius: 14, overflow: "hidden", background: C.cardGradient, cursor: "pointer", textAlign: "left",
          fontFamily: "inherit", color: C.text }}>
        {/* The real next section, blurred: chapter titles and the first case. */}
        <div aria-hidden="true" style={{ padding: "20px 22px 24px", filter: "blur(4.5px)", opacity: 0.55, userSelect: "none",
          pointerEvents: "none" }}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>{chapters[0]}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {chapters.map(t => (
              <span key={t} style={{ fontSize: 11.5, padding: "4px 10px", borderRadius: 999, border: `1px solid ${C.border}`,
                color: C.textMid }}>{t}</span>
            ))}
          </div>
          {ex && (
            <div style={{ padding: "14px 16px", borderRadius: 12, border: `1px solid ${GOLD}44`, background: `${GOLD}0d` }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 10, lineHeight: 1.5 }}>{plain(ex.prompt || ex.intro)}</div>
              {Array.isArray(ex.options) && ex.options.length
                ? ex.options.map(o => (
                  <div key={o.id} style={{ fontSize: 13, color: C.textMid, padding: "8px 12px", borderRadius: 9,
                    border: `1px solid ${C.border}`, marginBottom: 6 }}>{plain(o.label)}</div>))
                : <div style={{ height: 70, borderRadius: 9, border: `1px solid ${C.border}`, background: "rgba(0,0,0,0.25)" }}/>}
            </div>
          )}
        </div>
        <span style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 10, background: "linear-gradient(180deg, rgba(10,10,10,0.15), rgba(10,10,10,0.65))" }}>
          <span style={{ width: 46, height: 46, borderRadius: 23, display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(10,10,10,0.75)", border: `1px solid ${GOLD}88` }}>
            <Lock size={19} color={GOLD}/>
          </span>
          <span style={{ fontSize: 13.5, fontWeight: 800, color: "#F5F5F5", textAlign: "center", padding: "0 16px" }}>
            Unlock the deep dive
          </span>
        </span>
      </button>
      {admin && (
        <div style={{ fontSize: 11.5, color: C.textDim, marginTop: 6 }}>
          Admin preview: coaches not in the Certification see this. Enrolled coaches get a direct link to the lesson; on iOS, coaches who aren't enrolled see nothing here.
        </div>
      )}

      {/* Portaled to the page: the reader is a transformed container, and a
          fixed overlay inside it is positioned against that, not the screen. */}
      {open && createPortal((
        <div role="dialog" aria-modal="true" aria-label="The BAM Coaches Certification" onClick={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 7000, background: "rgba(0,0,0,0.7)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", width: "100%", maxWidth: 460, borderRadius: 18, padding: "26px 24px 22px",
              background: "#121212", border: `1px solid ${GOLD}55`, boxShadow: "0 30px 80px rgba(0,0,0,0.6)", color: "#F5F5F5" }}>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"
              style={{ position: "absolute", top: 12, right: 12, width: 36, height: 36, borderRadius: 18, border: "none",
                background: "transparent", color: "#9CA3AF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <X size={18}/>
            </button>
            <div style={{ ...eyebrow, marginBottom: 8 }}>BAM Coaches Certification</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, letterSpacing: 1.2, lineHeight: 1, marginBottom: 12 }}>
              The deeper version of this concept
            </div>
            <div style={{ fontSize: 14, color: "#C9C9C9", lineHeight: 1.6, marginBottom: 14 }}>
              What you just read is the Platform version. The Certification goes further: the science behind the idea,
              case studies you answer before you see how we'd think about them, and an exercise you work on your own
              players. Your answers save to your account.
            </div>
            <div style={{ fontSize: 12.5, color: "#9CA3AF", marginBottom: 6 }}>{mod.title}{mod.sub ? `: ${mod.sub}` : ""} · {mod.duration}</div>
            <ul style={{ margin: "0 0 20px", paddingLeft: 18 }}>
              {chapters.map(t => <li key={t} style={{ fontSize: 13.5, color: "#E8E8E8", lineHeight: 1.7 }}>{t}</li>)}
            </ul>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" className="btn" onClick={() => { window.location.hash = "#/certified"; }}
                style={{ minHeight: 44, padding: "0 18px", borderRadius: 10, border: "none", background: GOLD, color: "#111",
                  fontSize: 14, fontWeight: 800, fontFamily: "inherit", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}>
                See the Certification <ArrowRight size={15}/>
              </button>
              <button type="button" className="btn" onClick={() => setOpen(false)}
                style={{ minHeight: 44, padding: "0 16px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)",
                  background: "transparent", color: "#C9C9C9", fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                Not now
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

export default CertTeaser;
